import { readConsentPreferences } from './consent';

export type AnalyticsEventName =
  | 'cta_click'
  | 'category_click'
  | 'category_filter'
  | 'collection_filter_click'
  | 'generate_lead'
  | 'lead_form_start'
  | 'lead_form_blocked'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'checkout_blocked'
  | 'no_result_view'
  | 'quiz_started'
  | 'quiz_completed'
  | 'scroll_depth'
  | 'select_item'
  | 'sign_up'
  | 'supplier_gate_view'
  | 'view_item'
  | 'form_start'
  | 'form_submit_attempt'
  | 'form_blocked'
  | 'experiment_impression'
  | 'consent_update';

export type AnalyticsEvent = {
  event: AnalyticsEventName;
  location: string;
  label?: string;
  value?: string;
  sku?: string;
  category?: string;
  brand?: string;
  matchType?: string;
  routeFamily?: string;
  placement?: string;
  eventId?: string;
  tenantId?: 'propickleball';
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export const measurementEvents: AnalyticsEventName[] = [
  'cta_click',
  'category_click',
  'category_filter',
  'collection_filter_click',
  'generate_lead',
  'lead_form_start',
  'lead_form_blocked',
  'add_to_cart',
  'begin_checkout',
  'checkout_blocked',
  'no_result_view',
  'quiz_started',
  'quiz_completed',
  'scroll_depth',
  'select_item',
  'sign_up',
  'supplier_gate_view',
  'view_item',
  'form_start',
  'form_submit_attempt',
  'form_blocked',
  'experiment_impression',
  'consent_update'
];

function analyticsExportAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  return readConsentPreferences()?.analytics === true;
}

export function trackEvent(event: AnalyticsEvent): void {
  if (!analyticsExportAllowed()) return;

  const safeDimension = (value?: string) =>
    value && /^[a-zA-Z0-9_:/.-]{1,80}$/.test(value) ? value : undefined;
  window.dataLayer = window.dataLayer || [];
  const eventId = safeDimension(event.eventId)
    || globalThis.crypto?.randomUUID?.()
    || `evt_${Date.now()}`;
  if (window.dataLayer.some((entry) => entry.event_id === eventId)) return;
  const payload = {
    event: event.event,
    brand_alias: 'propickleball',
    tenant_id: event.tenantId ?? 'propickleball',
    event_id: eventId,
    event_source: 'browser',
    consent_state: 'granted',
    event_location: safeDimension(event.location),
    event_label: safeDimension(event.label),
    event_value: safeDimension(event.value),
    item_id: safeDimension(event.sku),
    item_category: safeDimension(event.category),
    item_brand: safeDimension(event.brand),
    match_type: safeDimension(event.matchType),
    route_family: safeDimension(event.routeFamily),
    placement: safeDimension(event.placement),
    checkout_ready: false,
  };
  window.dataLayer.push(payload);
}
